import { Actions as ADVISOR_ACTIONS } from "@/redux/actions/advisorActions";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { TimeInputFormat } from "@/components/common/DateComponents";
import { validateInteger } from "@/components/common/validation/Validators";
import { addHours, format, set } from "date-fns";
import { isEqual, merge, mergeWith, setWith, uniqWith } from "lodash";
import { useEffect, useMemo, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { connect } from "react-redux";
import ReactSelect from "react-select";

const dateReferenceOptions = [
	{ value: "sameDay", label: "Same Day As" },
	{ value: "before", label: "Before" },
	{ value: "after", label: "After", isDisabled: (v) => !v },
];

const dateOptions = [
	{ value: "payDay", label: "Pay Day" },
	{ value: "periodEnd", label: "End of Period" },
	{ value: "deadline", label: "Deadline" },
];

const formatTime = (time) => {
	try {
		let newTime;
		if (time instanceof Date) newTime = time;
		else newTime = new Date(time);
		return format(newTime, "HH:mm");
	} catch {
		return undefined;
	}
};

const EverRollModal = ({
	modalStatus,
	closeModal,
	org,
	paySchedule,
	actions,
	everRollRule,
	saveEverRollSettings,
	saving,
}) => {
	const [data, setData] = useState({});
	const [saved, setSaved] = useState(true);
	const [closing, setClosing] = useState(false);

	useEffect(() => {
		if (!modalStatus) {
			setData({});
		} else {
			setSaved(true);
			setData({ ...everRollRule });
		}
	}, [modalStatus]);

	const scheduledActions = Object.entries(data || {}).map(([key, value]) => {
		if (!value) return null;
		return {
			days: value.days,
			condition: value.condition,
			referenceDate: value.referenceDate,
			time: value.time,
			action: key,
		};
	});

	const [similarActions, actionsEarlierThanRequired] = useMemo(() => {
		const similarActions = scheduledActions?.reduce((acc, action) => {
			const existingAction = scheduledActions.find((a) => {
				const sameDay = a?.days === action?.days && a?.condition === action?.condition && a?.referenceDate === action?.referenceDate;
				const sameTime = formatTime(a?.time) === formatTime(action?.time);
				const inEqualAction = a?.action !== action?.action;
				return sameDay && sameTime && inEqualAction;
			});

			if (existingAction) return [...acc, action];
			return acc;
		}, []);

		const similar = similarActions?.length > 1 ? similarActions?.map((a) => a?.action) : [];

		const actionsEarlierThanRequired = scheduledActions?.filter((a) => {
			const action = actions?.find((i) => i?.action === a?.action);
			const required = scheduledActions?.find((i) => i?.action === action?.required);

			if (!required) return false;
			const sameDay =
				required?.days === a?.days &&
				required?.condition === a?.condition &&
				required?.referenceDate === a?.referenceDate;
			const afterTime = formatTime(required?.time) < formatTime(a?.time);
			return sameDay && !afterTime;
		});

		return [similar, actionsEarlierThanRequired?.length];
	}, [JSON.stringify(scheduledActions), JSON.stringify(actions)]);

	const updateField = (field, value) => {
		if (!field) return;
		if (field.includes("[")) field = setWith({}, field, value, Object);

		if (saved) setSaved(false);

		const newData = mergeWith(
			data,
			{ ...(typeof field == "object" ? field : { [field]: value }) },
			(objValue, srcValue, key, object, source) => {
				if (Array.isArray(objValue) || Array.isArray(srcValue)) {
					const sr = Array.isArray(srcValue) ? srcValue : [];
					return uniqWith([...sr], isEqual);
				}
			},
		);

		setData({ ...newData });
	};

	if (!modalStatus) return null;

	return (
		<>
			<div
				className="modal"
				tabIndex="-1"
				role="dialog"
				aria-hidden="true"
				style={{ display: "block", background: "rgba(0,0,0,0.4)", overflow: "auto" }}
			>
				<div
					className="modal-dialog"
					style={{ width: "60%" }}
				>
					<div className="modal-content">
						<div
							className="block block-themed block-transparent remove-margin-b"
							style={{ padding: 0 }}
						>
							<div
								className="block-header bg-primary-dark"
								style={{ padding: 20, position: "sticky", top: 0, zIndex: 50 }}
							>
								<ul className="block-options">
									<li>
										<button
											type="button"
											onClick={() => {
												if (!saved) setClosing(true);
												else closeModal();
											}}
										>
											<XMarkIcon className="h-6 w-6 mr-3 text-white" />
										</button>
									</li>
								</ul>
								<h3 className="block-title">Setup EverRoll™</h3>
							</div>
							<form
								onSubmit={(e) => {
									e.preventDefault();
									saveEverRollSettings(data);
									setSaved(true);
								}}
							>
								<div className="block-content">
									Setup EverRoll™ for {org?.name} - {paySchedule?.name}
									{actions?.map((action) => {
										const actionData = data?.[action.action];
										const updateActionData = (field, value) =>
											updateField(`${action.action}[${field}]`, value);

										const required = action?.required ? data[action?.required] : false;

										const sameDay =
											required?.days === actionData?.days &&
											required?.condition === actionData?.condition &&
											required?.referenceDate === actionData?.referenceDate;
										const afterTime = formatTime(required?.time) < formatTime(actionData?.time);

										const restrictions = actionData?.restrictions || [];

										const updateRestrictions = (restriction) => {
											const newRestrictions = [...restrictions];
											if (newRestrictions.includes(restriction)) {
												newRestrictions.splice(newRestrictions.indexOf(restriction), 1);
											} else {
												newRestrictions.push(restriction);
											}
											updateActionData("restrictions", newRestrictions);
										};

										const toggleAction = (e) => {
											if (e.target.checked) {
												updateField(action.action, {
													days: "",
													condition: "sameDay",
													referenceDate: "payDay",
													time: set(new Date(), {
														minutes: 0,
														milliseconds: 0,
														seconds: 0,
													}).toISOString(),
												});
											} else {
												updateField(action.action, null);
											}
										};

										const isSimilar = similarActions?.includes(action.action);

										return (
											<div
												className="form-section"
												style={{ paddingBottom: "2rem" }}
											>
												<div className="col-xs-4 pl-0">
													<label className="css-input switch switch-sm switch-success">
														<input
															type="checkbox"
															className="checkbox-toggle"
															checked={!!actionData}
															onChange={toggleAction}
														/>
														<span /> {action.name}
													</label>
												</div>
												<div className="col-xs-8 pl-0">
													{isSimilar ? (
														<div
															className="badge badge-danger pull-right"
															style={{ marginTop: 2, marginRight: 2 }}
														>
															Actions on the same date & time will interfere with their
															execution.
														</div>
													) : null}
													{actionData && sameDay && !afterTime ? (
														<div
															className="badge badge-danger pull-right"
															style={{ marginTop: 2, marginRight: 2 }}
														>
															Must be after{" "}
															{actions?.find((i) => i.action === action.required)?.name}
														</div>
													) : null}
												</div>
												<div className="clearfix" />
												{!!actionData && (
													<div>
														<h5>Execute Action "{action.name}"</h5>
														<div className="form-inner-section">
															<div className="col-xs-2">
																<div className="form-material">
																	<label htmlFor="days">Days</label>
																	<input
																		className="form-control form-input text-right"
																		type="text"
																		id="days"
																		disabled={actionData.condition === "sameDay"}
																		placeholder="days"
																		name="days"
																		required={actionData.condition !== "sameDay"}
																		value={actionData?.days}
																		onChange={(e) => {
																			const value = validateInteger(
																				e.target.value,
																			);
																			updateActionData("days", value);
																		}}
																	/>
																</div>
															</div>
															<div className="col-xs-3 pl-0">
																<div className="form-material">
																	<ReactSelect
																		options={dateReferenceOptions?.map((o) => ({
																			...o,
																			isDisabled: o.isDisabled?.(
																				action?.canBeAfterPeriod,
																			),
																		}))}
																		value={dateReferenceOptions.find(
																			(o) => o.value === actionData?.condition,
																		)}
																		onChange={(e) => {
																			updateActionData(
																				"condition",
																				e?.value || null,
																			);
																			if (e?.value === "sameDay") {
																				updateActionData("days", "");
																			}
																		}}
																	/>
																</div>
															</div>
															<div className="col-xs-3 pl-0">
																<div className="form-material">
																	<label htmlFor="referenceDate">Date</label>
																	<ReactSelect
																		options={dateOptions}
																		value={dateOptions.find(
																			(o) =>
																				o.value === actionData?.referenceDate,
																		)}
																		onChange={(e) =>
																			updateActionData(
																				"referenceDate",
																				e?.value || null,
																			)
																		}
																	/>
																</div>
															</div>
															<div className="col-xs-3 pl-0">
																<div className="form-material">
																	<label htmlFor="time">At</label>
																	<ReactDatePicker
																		onChange={(time) => {
																			updateActionData(
																				"time",
																				time.toISOString(),
																			);
																		}}
																		showTimeSelect
																		selected={new Date(actionData?.time)}
																		showTimeSelectOnly
																		minDate={
																			required?.time
																				? new Date(required?.time)
																				: null
																		}
																		maxDate={
																			required?.time
																				? new Date(required?.time)
																				: null
																		}
																		minTime={
																			required?.time
																				? addHours(new Date(required?.time), 1)
																				: null
																		}
																		maxTime={
																			required?.time
																				? addHours(new Date(required?.time), 23)
																				: null
																		}
																		timeIntervals={60}
																		timeCaption="Hour"
																		dateFormat="h:mm aa"
																		customInput={<TimeInputFormat />}
																	/>
																</div>
															</div>
															<div className="clearfix" />
														</div>
														{action?.restrictions?.length > 0 ? (
															<>
																<h5>Do Not Execute Action if</h5>
																<div className="form-inner-section">
																	{action?.restrictions?.map((restriction) => {
																		return (
																			<div className="col-xs-12 pl-0">
																				<label
																					className="css-input css-checkbox css-checkbox-primary"
																					style={{ marginBottom: "0.5rem" }}
																				>
																					<input
																						type="checkbox"
																						name={restriction.restriction}
																						checked={restrictions.includes(
																							restriction.restriction,
																						)}
																						onClick={() =>
																							updateRestrictions(
																								restriction.restriction,
																							)
																						}
																					/>
																					<span></span>
																					{restriction.name} &nbsp;
																				</label>
																				<div className="clearfix" />
																			</div>
																		);
																	})}
																	<div className="clearfix" />
																</div>
															</>
														) : null}
													</div>
												)}
											</div>
										);
									})}
									<div
										className="col-xs-12 pr-0"
										style={{ paddingBottom: "2rem" }}
									>
										{similarActions?.length > 0 || actionsEarlierThanRequired ? (
											<button
												className="btn btn-danger pull-right"
												type="button"
												onClick={() => {}}
											>
												Invalid Action Configurations
											</button>
										) : (
											<button
												className="btn btn-success pull-right"
												type="submit"
												disabled={saving}
											>
												{saving ? "Saving" : "Save"} EverRoll™ Settings
											</button>
										)}
									</div>
								</div>
							</form>
							<div className="clearfix" />
						</div>
					</div>
				</div>
			</div>
			{!saved && closing && (
				<div
					className="modal"
					tabIndex="-1"
					role="dialog"
					aria-hidden="true"
					style={{ display: "block", background: "rgba(0,0,0,0.4)", overflow: "auto" }}
				>
					<div
						className="modal-dialog"
						style={{ width: "60%" }}
					>
						<div className="modal-content">
							<div
								className="block block-themed block-transparent remove-margin-b"
								style={{ padding: 0 }}
							>
								<div
									className="block-header bg-primary-dark"
									style={{ padding: 20, position: "sticky", top: 0, zIndex: 50 }}
								>
									<ul className="block-options">
										<li>
											<button
												type="button"
												onClick={() => {
													if (!saved) setClosing(true);
													else closeModal();
												}}
											>
												<XMarkIcon className="h-6 w-6 mr-3 text-white" />
											</button>
										</li>
									</ul>
									<h3 className="block-title">Setup EverRoll™</h3>
								</div>
								<div className="block-content bg-white">
									Yikes! You have unsaved changes. Are you sure you want to close?
									<div
										className="col-xs-12 pr-0"
										style={{ marginBottom: "2rem" }}
									>
										<button
											className="btn btn-danger pull-right"
											onClick={() => {
												setClosing(false);
												closeModal();
											}}
										>
											Yes, Close
										</button>
										<button
											className="btn btn-success pull-right"
											onClick={() => setClosing(false)}
											style={{ marginRight: "1rem" }}
										>
											No, Stay
										</button>
										<div className="clearfix" />
									</div>
								</div>
								<div className="clearfix" />
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

const mapStateToProps = (state) => {
	const everRollSettingsModal = state?.advisors?.everRollSettingsModal;
	const { orgPaySchedules, everRollRules, actions } = state.advisors.everRollSettings || {};

	const { org, schedule } = everRollSettingsModal instanceof Object ? everRollSettingsModal : {};

	const matchingOrg = orgPaySchedules?.find((o) => o?.org == org);

	const matchingPaySchedule = matchingOrg?.paySchedules?.find((p) => p?.id == schedule);
	const everRollRule = everRollRules?.[org]?.find?.((r) => r?.entity == schedule)?.actions || {};

	return {
		everRollRule,
		actions: actions?.filter((a) => !a.jurisdiction || a.jurisdiction?.includes(matchingOrg?.jurisdiction)),
		org: matchingOrg,
		saving: state?.advisors?.savingEverRollSettings,
		paySchedule: matchingPaySchedule,
		modalStatus: org && schedule ? `${org}-${schedule}` : null,
	};
};

const mapDispatchToProps = (dispatch) => ({
	saveEverRollSettings: (settings) => dispatch(ADVISOR_ACTIONS.saveEverRollSettings(settings)),
	closeModal: () => dispatch(ADVISOR_ACTIONS.openEverRollModal(false)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EverRollModal);
